import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, X, CheckCircle, AlertCircle } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

export default function UploadASDRoster({ onClose, onSuccess }) {
  const [manualData, setManualData] = useState("");
  const [message, setMessage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const queryClient = useQueryClient();

  const { data: settings = [] } = useQuery({
    queryKey: ['settings'],
    queryFn: () => base44.entities.Settings.list(),
  });

  const exclusionSettings = settings.find(s => s.setting_key === 'excluded_students') || {
    excluded_student_ids: []
  };

  const excludedIds = exclusionSettings.excluded_student_ids || [];

  const processManualData = async () => {
    if (!manualData.trim()) {
      setMessage({ type: 'error', text: 'Please paste your ASD roster data' });
      return;
    }

    setProcessing(true);
    setMessage({ type: 'info', text: 'Parsing data...' });

    try {
      const lines = manualData.trim().split('\n').map(line => line.trim()).filter(line => line);
      
      if (lines.length === 0) {
        throw new Error('No data found');
      }

      // Parse header line
      const headerLine = lines[0];
      const headers = headerLine.split('\t').map(h => h.trim().toLowerCase());
      
      // Map column indices
      const colMap = {
        campus: headers.findIndex(h => h.includes('campus')),
        studentLast: headers.findIndex(h => h.includes('student last name')),
        studentFirst: headers.findIndex(h => h.includes('student first name')),
        otherId: headers.findIndex(h => h.includes('other id')),
        studentId: headers.findIndex(h => h === 'student id'),
        methodOfInstruction: headers.findIndex(h => h.includes('method of instruction')),
        gradeLevel: headers.findIndex(h => h.includes('grade level')),
        course: headers.findIndex(h => h === 'course'),
        section: headers.findIndex(h => h === 'section'),
        courseShortDesc: headers.findIndex(h => h.includes('course short description')),
        teacher: headers.findIndex(h => h === 'teacher'),
        teacherEmail: headers.findIndex(h => h.includes('teacher email')),
        assignment: headers.findIndex(h => h === 'assignment'),
        assignmentGrade: headers.findIndex(h => h.includes('assignment grade')),
        athleticGrade: headers.findIndex(h => h.includes('athletic grade')),
        comments: headers.findIndex(h => h === 'comments'),
        date: headers.findIndex(h => h === 'date'),
        time: headers.findIndex(h => h === 'time'),
        f1g1Phone: headers.findIndex(h => h.includes('f1g1 parent phone')),
        f1g2Phone: headers.findIndex(h => h.includes('f1g2 parent phone')),
        studentEmail: headers.findIndex(h => h.includes('student email')),
        f1g1Email: headers.findIndex(h => h.includes('f1g1 parent email')),
        f2g1Phone: headers.findIndex(h => h.includes('f2g1 parent phone')),
        f2g1Email: headers.findIndex(h => h.includes('f2g1 parent email')),
      };

      if (colMap.otherId === -1 || colMap.studentLast === -1 || colMap.teacher === -1) {
        throw new Error('Missing required columns (Other ID, Student Last Name, or Teacher)');
      }

      setMessage({ type: 'info', text: 'Processing entries...' });

      let addedCount = 0;
      let excludedCount = 0;
      let errorCount = 0;
      const entriesToCreate = [];

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        const cols = line.split('\t').map(c => c.trim());

        const otherId = cols[colMap.otherId];

        // Skip if excluded
        if (excludedIds.includes(otherId)) {
          excludedCount++;
          continue;
        }

        const studentLast = cols[colMap.studentLast];
        const studentFirst = colMap.studentFirst !== -1 ? cols[colMap.studentFirst] : '';
        const teacher = cols[colMap.teacher];
        const teacherEmail = colMap.teacherEmail !== -1 ? cols[colMap.teacherEmail] : '';

        // Skip if missing critical data
        if (!otherId || !studentLast || !teacher) {
          errorCount++;
          continue;
        }

        entriesToCreate.push({
          assignment_type: 'automatic',
          campus: colMap.campus !== -1 ? cols[colMap.campus] : '',
          student_last_name: studentLast,
          student_first_name: studentFirst,
          other_id: otherId,
          student_id: colMap.studentId !== -1 ? cols[colMap.studentId] : '',
          method_of_instruction: colMap.methodOfInstruction !== -1 ? cols[colMap.methodOfInstruction] : '',
          grade_level: colMap.gradeLevel !== -1 ? cols[colMap.gradeLevel] : '',
          course: colMap.course !== -1 ? cols[colMap.course] : '',
          section: colMap.section !== -1 ? cols[colMap.section] : '',
          course_short_description: colMap.courseShortDesc !== -1 ? cols[colMap.courseShortDesc] : '',
          teacher: teacher,
          teacher_email: teacherEmail,
          assignment: colMap.assignment !== -1 ? cols[colMap.assignment] : '',
          assignment_grade: colMap.assignmentGrade !== -1 ? cols[colMap.assignmentGrade] : '',
          athletic_grade: colMap.athleticGrade !== -1 ? cols[colMap.athleticGrade] : '',
          comments: colMap.comments !== -1 ? cols[colMap.comments] : '',
          date: colMap.date !== -1 ? cols[colMap.date] : '',
          time: colMap.time !== -1 ? cols[colMap.time] : '',
          f1g1_parent_phone: colMap.f1g1Phone !== -1 ? cols[colMap.f1g1Phone] : '',
          f1g2_parent_phone: colMap.f1g2Phone !== -1 ? cols[colMap.f1g2Phone] : '',
          student_email: colMap.studentEmail !== -1 ? cols[colMap.studentEmail] : '',
          f1g1_parent_email: colMap.f1g1Email !== -1 ? cols[colMap.f1g1Email] : '',
          f2g1_parent_phone: colMap.f2g1Phone !== -1 ? cols[colMap.f2g1Phone] : '',
          f2g1_parent_email: colMap.f2g1Email !== -1 ? cols[colMap.f2g1Email] : '',
          status: 'asd',
          attendance_status: 'pending'
        });

        addedCount++;
      }

      // Bulk create all entries
      setMessage({ type: 'info', text: `Creating ${entriesToCreate.length} entries...` });
      const batchSize = 100;
      for (let i = 0; i < entriesToCreate.length; i += batchSize) {
        const batch = entriesToCreate.slice(i, i + batchSize);
        await base44.entities.RosterEntry.bulkCreate(batch);
      }

      // Remove duplicates
      setMessage({ type: 'info', text: 'Removing duplicates...' });
      const allASD = await base44.entities.RosterEntry.filter({ status: 'asd' });
      const seen = new Map();
      const duplicateIds = [];

      for (const entry of allASD) {
        const key = `${entry.other_id}|${entry.teacher}|${entry.course_short_description}`;
        if (seen.has(key)) {
          duplicateIds.push(entry.id);
        } else {
          seen.set(key, entry.id);
        }
      }

      if (duplicateIds.length > 0) {
        const deleteBatchSize = 100;
        for (let i = 0; i < duplicateIds.length; i += deleteBatchSize) {
          const batch = duplicateIds.slice(i, i + deleteBatchSize);
          await Promise.all(batch.map(id => base44.entities.RosterEntry.delete(id)));
        }
      }

      const successMsg = `✅ ASD Roster Upload Complete!\n\n` +
        `📊 Total Rows: ${lines.length - 1}\n` +
        `✅ Added to ASD: ${addedCount}\n` +
        `⏭️ Excluded: ${excludedCount}\n` +
        `🗑️ Duplicates Removed: ${duplicateIds.length}\n` +
        `❌ Errors: ${errorCount}`;

      setMessage({ type: 'success', text: successMsg });
      queryClient.invalidateQueries(['rosterEntries']);
      
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 3000);

    } catch (err) {
      console.error('Processing error:', err);
      setMessage({
        type: 'error',
        text: `❌ Upload failed: ${err.message || 'Failed to process data'}`
      });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <Card className="w-full max-w-2xl my-8">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <CardTitle>Upload ASD Roster</CardTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              disabled={processing}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <Alert className="bg-blue-50 border-blue-200">
            <AlertDescription className="text-blue-900">
              <strong>📋 Upload Instructions:</strong>
              <ol className="list-decimal list-inside mt-2 space-y-1 text-sm ml-2">
                <li>Open your ASD roster file in Excel</li>
                <li><strong>Select ALL columns</strong> from header row down</li>
                <li>Copy (Ctrl+C or Cmd+C)</li>
                <li>Paste below (Ctrl+V or Cmd+V)</li>
                <li>Click "Process ASD Roster"</li>
              </ol>
              <div className="text-xs text-blue-700 mt-2">
                ℹ️ System automatically removes duplicates (same Other ID + Teacher + Course). Excluded students are skipped.
              </div>
            </AlertDescription>
          </Alert>

          {message && (
            <Alert className={
              message.type === 'success' ? 'bg-green-50 border-green-200' :
              message.type === 'error' ? 'bg-red-50 border-red-200' :
              'bg-blue-50 border-blue-200'
            }>
              {message.type === 'success' ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : message.type === 'error' ? (
                <AlertCircle className="w-4 h-4 text-red-600" />
              ) : (
                <AlertCircle className="w-4 h-4 text-blue-600" />
              )}
              <AlertDescription className={
                message.type === 'success' ? 'text-green-900' :
                message.type === 'error' ? 'text-red-900' :
                'text-blue-900'
              } style={{ whiteSpace: 'pre-line' }}>
                {message.text}
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Paste ASD Roster Data
              </label>
              <Textarea
                value={manualData}
                onChange={(e) => {
                  setManualData(e.target.value);
                  setMessage(null);
                }}
                placeholder="Paste complete ASD roster with ALL columns here..."
                className="h-64 font-mono text-xs"
                disabled={processing}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={processing}
              >
                Cancel
              </Button>
              <Button
                onClick={processManualData}
                className="flex-1 bg-red-600 hover:bg-red-700"
                disabled={!manualData.trim() || processing}
              >
                {processing ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Process ASD Roster
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}